import { createContext, useContext, useEffect, useState, ReactNode } from "react";

type Language = {
  code: string;
  name: string;
  native_name: string;
  flag: string;
};

const defaultLanguages: Language[] = [
  { code: "de", name: "German", native_name: "Deutsch", flag: "🇩🇪" },
  { code: "en", name: "English", native_name: "English", flag: "🇬🇧" },
  { code: "es", name: "Spanish", native_name: "Español", flag: "🇪🇸" },
  { code: "fr", name: "French", native_name: "Français", flag: "🇫🇷" },
  { code: "zh", name: "Chinese", native_name: "中文", flag: "🇨🇳" },
  
];

interface LanguageContextType {
  currentLang: string;
  setCurrentLang: (lang: string) => void;
  languages: Language[];
  t: (translations: Record<string, string>, fallback?: string) => string;
}

const LanguageContext = createContext<LanguageContextType>({
  currentLang: "de",
  setCurrentLang: () => {},
  languages: defaultLanguages,
  t: () => "",
});

export const useLanguage = () => useContext(LanguageContext);

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const [currentLang, setCurrentLangState] = useState(() => {
    const stored = localStorage.getItem("lang");
    return stored || "de";
  });

  const applyLangAttrs = (lang: string) => {
    document.documentElement.lang = lang;
    document.documentElement.dir = "ltr";
    document.documentElement.dataset.lang = lang;
  };

  const setCurrentLang = (lang: string) => {
    if (lang === currentLang) return;
    // Brief fade-out for smooth transition; PageTranslator restores opacity.
    document.documentElement.classList.add("translating");
    setCurrentLangState(lang);
    localStorage.setItem("lang", lang);
    applyLangAttrs(lang);
    window.setTimeout(() => {
      document.documentElement.classList.remove("translating");
    }, 600);
  };

  useEffect(() => {
    applyLangAttrs(currentLang);
  }, [currentLang]);

  const t = (translations: Record<string, string>, fallback = "") => {
    return translations[currentLang] || translations["de"] || fallback;
  };

  return (
    <LanguageContext.Provider value={{ currentLang, setCurrentLang, languages: defaultLanguages, t }}>
      {children}
    </LanguageContext.Provider>
  );
};
